// packages/studio/web/src/proposals/StateBadge.tsx
//
// Shared status pill for changeset state. One source of truth for the colors
// used across the editor bar, list, detail, and switcher.

import { cx } from "../lib/classNames";
import type { ChangesetState } from "./proposalsApi";
import { useTranslation } from "../i18n";

const STYLES: Record<ChangesetState, { dot: string; chip: string; labelKey: string }> = {
  draft: { dot: "bg-slate-400", chip: "bg-slate-800 text-slate-300 ring-slate-700", labelKey: "state.draft" },
  proposed: { dot: "bg-arch-400", chip: "bg-arch-500/15 text-arch-200 ring-arch-500/30", labelKey: "state.open" },
  accepted: { dot: "bg-emerald-400", chip: "bg-emerald-500/15 text-emerald-200 ring-emerald-500/30", labelKey: "state.accepted" },
  archived: { dot: "bg-slate-500", chip: "bg-slate-800/60 text-slate-400 ring-slate-700/60", labelKey: "state.archived" },
};

export function StateBadge({
  state,
  mine,
  className,
}: {
  readonly state: ChangesetState;
  /** When set, an open change is labelled by ownership: yours vs someone else's. */
  readonly mine?: boolean;
  readonly className?: string;
}) {
  const { t } = useTranslation("proposals");
  const s = STYLES[state];
  // Distinguish your own open changes from others' (same visuals, different word).
  const label =
    state === "proposed" && mine !== undefined
      ? mine
        ? t("state.authored")
        : t("state.open")
      : t(s.labelKey);
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
        s.chip,
        className,
      )}
    >
      <span className={cx("h-1.5 w-1.5 rounded-full", s.dot)} aria-hidden="true" />
      {label}
    </span>
  );
}
